import { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys';
import {
    MRIMachinePopulated,
    MachineFavorite,
    MachineFilterOptions,
    MachineFilterState,
    NO_LOCATION,
} from './types';
import {
    buildFilterOptions,
    filterMachines,
    groupByLocation,
} from './roomOverviewUtils';
import LoadingRing from '../ui-components/LoadingRing';
import OverlayWindow from '../ui-components/OverlayWindow';
import { Button } from '../ui/button';
import { PlusSquare } from 'lucide-react';
import MRIMachine from './MRIMachine';
import MachineGrid from './MachineGrid';
import MachineFilters from './MachineFilters';
import MachineListTable from './MachineListTable';
import { ViewModeToggle, ViewMode } from '../ui-components/ViewModeToggle';
import { getMachineFavorites } from '../../services/api/mriMachineFavouriteService';
import { getAllMRIMachinesByDepartment } from '../../services/apiService';
import {
    getRoomPreferences,
    saveRoomPreferences,
} from '../../services/api/userPreferencesService';
import { EVENT_KEYS, trackClick } from '../../services/api/trackingService';

export default function RoomOverview() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const canManageMRI = authCtx.orRights([RIGHTS_KEYS.manageMRIOverview]);
    const [isLoading, setIsLoading] = useState(false);
    const [machines, setMachines] = useState<MRIMachinePopulated[]>([]);
    const [machineIdToEdit, setMachineIdToEdit] = useState('');
    const [filterOptions, setFilterOptions] =
        useState<MachineFilterOptions | null>(null);
    const [filters, setFilters] = useState<MachineFilterState>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [favoritedMachineIds, setFavoritedMachineIds] = useState<Set<string>>(
        new Set()
    );
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
        new Set()
    );
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const hydratedRef = useRef(false);

    const closeMachineOverlay = () => {
        setMachineIdToEdit('');
    };

    const getData = async () => {
        setIsLoading(true);
        try {
            // Fetch machines
            const machinesData = await getAllMRIMachinesByDepartment();

            const machines: MRIMachinePopulated[] = Array.isArray(machinesData)
                ? machinesData
                : [];

            if (machines.length === 0) {
                setMachines([]);
                setIsLoading(false);
                return;
            }

            setMachines(machines);

            setFilterOptions(buildFilterOptions(machines));

            // Fetch favorites
            try {
                const favoritesData = await getMachineFavorites();
                setFavoritedMachineIds(
                    new Set(
                        favoritesData?.map(
                            (f: MachineFavorite) => f.machine?._id
                        ) || []
                    )
                );
            } catch {
                console.error('Failed to load favorites');
            }
        } catch (error) {
            console.error('Failed to load machines:', error);
            setMachines([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFavoriteChange = (machineId: string, isFavorited: boolean) => {
        setFavoritedMachineIds((prev) => {
            const next = new Set(prev);
            if (isFavorited) {
                next.add(machineId);
            } else {
                next.delete(machineId);
            }
            return next;
        });
    };

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const hydrate = async () => {
            try {
                const prefs = await getRoomPreferences();
                if (prefs.view) setViewMode(prefs.view);
            } catch {
            } finally {
                hydratedRef.current = true;
            }
        };
        hydrate();
    }, []);

    useEffect(() => {
        if (!hydratedRef.current) return;
        const handle = setTimeout(() => {
            const save = async () => {
                try {
                    await saveRoomPreferences({ view: viewMode });
                } catch {}
            };
            save();
        }, 500);
        return () => clearTimeout(handle);
    }, [viewMode]);

    const filteredMachines = useMemo(
        () =>
            filterMachines(machines, searchTerm, filters, favoritedMachineIds),
        [machines, searchTerm, filters, favoritedMachineIds]
    );

    const totalMachines = filteredMachines.length;

    const sections = useMemo(
        () => groupByLocation(filteredMachines),
        [filteredMachines]
    );

    const hasLocations = sections.some(
        (section) => section.key !== NO_LOCATION
    );

    const toggleSection = (key: string) => {
        setCollapsedSections((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    return (
        <div className="container mx-auto max-w-7xl px-4 pt-6 pb-28 space-y-6 min-h-screen">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">
                        {t('roomOverview.roomOverview.title')}
                    </h1>
                    <p className="text-muted-foreground">
                        {t('roomOverview.roomOverview.subtitle')}
                    </p>
                </div>
                {canManageMRI && (
                    <div className="flex gap-2">
                        <Button
                            onClick={() => {
                                trackClick(EVENT_KEYS.clickCreateRoom, {});
                                navigate('/room-overview/create-room');
                            }}
                        >
                            <PlusSquare className="h-4 w-4" />
                            {t(
                                'roomOverview.roomOverview.buttons.addMRIMachine'
                            )}
                        </Button>
                    </div>
                )}
            </div>

            {/* Filters */}
            <MachineFilters
                filterOptions={filterOptions}
                filters={filters}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onFilterChange={setFilters}
                onReset={() => {
                    setFilters({});
                    setSearchTerm('');
                }}
                hasFavorites={favoritedMachineIds.size > 0}
            />

            {/* Detail/Edit Overlay */}
            {machineIdToEdit !== '' && (
                <OverlayWindow
                    close={closeMachineOverlay}
                    className="!bg-background md:!w-[55%] dark:!bg-neutral-900"
                >
                    <MRIMachine
                        machineId={machineIdToEdit}
                        onChange={getData}
                        onClose={closeMachineOverlay}
                        canEdit={canManageMRI}
                    />
                </OverlayWindow>
            )}

            {/* Loading State */}
            {isLoading && (
                <div className="flex justify-center py-12">
                    <LoadingRing size="large" />
                </div>
            )}

            {/* Empty State */}
            {!isLoading && totalMachines === 0 && (
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-subtle-foreground">
                        {searchTerm
                            ? t(
                                  'roomOverview.roomOverview.emptyState.noResults'
                              )
                            : t('roomOverview.roomOverview.emptyState.title')}
                    </h3>
                </div>
            )}

            {!isLoading && totalMachines > 0 && (
                <div className="flex justify-end">
                    <ViewModeToggle value={viewMode} onChange={setViewMode} />
                </div>
            )}

            {!isLoading && totalMachines > 0 && viewMode === 'list' && (
                <MachineListTable
                    sections={sections}
                    hasLocations={hasLocations}
                    collapsedSections={collapsedSections}
                    onToggleSection={toggleSection}
                    favoritedMachineIds={favoritedMachineIds}
                    onFavoriteChange={handleFavoriteChange}
                    onClick={setMachineIdToEdit}
                />
            )}

            {!isLoading && totalMachines > 0 && viewMode === 'grid' && (
                <MachineGrid
                    sections={sections}
                    hasLocations={hasLocations}
                    collapsedSections={collapsedSections}
                    onToggleSection={toggleSection}
                    favoritedMachineIds={favoritedMachineIds}
                    onFavoriteChange={handleFavoriteChange}
                    onClick={setMachineIdToEdit}
                />
            )}
        </div>
    );
}
